import { useLang, buildWhatsAppUrl, PHONE_DISPLAY, PHONE_E164, EMAIL } from "../i18n";
import { useReveal } from "../hooks/useReveal";
import { MessageCircle, Mail, Phone, Instagram } from "lucide-react";
import { toast } from "sonner";

export default function Footer() {
  const { t, lang, setLang } = useLang();
  const r = useReveal();

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast.success(`${EMAIL}`, { description: "Copied to clipboard" });
    } catch {
      window.location.href = `mailto:${EMAIL}`;
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#050505] pt-24 md:pt-32 pb-10 overflow-hidden">
      <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="container-x">
        {/* Big CTA */}
        <div ref={r} className="reveal">
          <p className="overline mb-6">06 — Contact</p>
          <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-[8rem] text-white">
            {t.footer.cta}
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="footer-book-btn"
              href={buildWhatsAppUrl(t.whatsapp_pretext)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa"
            >
              <MessageCircle className="w-4 h-4" /> {t.footer.book}
            </a>
            <button onClick={copyEmail} data-testid="footer-email-btn" className="btn-ghost">
              <Mail className="w-4 h-4" /> {t.footer.email}
            </button>
          </div>
        </div>

        {/* Info row */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-10 border-t border-white/10 pt-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">Studio</div>
            <div className="font-display text-2xl text-white">
              6<span style={{ color: "hsl(var(--red-2))" }}>i</span>nq Tattooz
            </div>
            <p className="mt-3 text-sm text-white/55 max-w-xs">{t.footer.built}</p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">Locations</div>
            <ul className="space-y-2 text-sm text-white/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5" style={{ background: "hsl(var(--coral))" }} />
                Willemstad, Curaçao
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5" style={{ background: "hsl(var(--mustard))" }} />
                Rotterdam, Netherlands
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">Contact</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  data-testid="footer-phone-link"
                  href={`tel:${PHONE_E164}`}
                  className="inline-flex items-center gap-2 text-white/80 hover:text-white"
                >
                  <Phone className="w-3.5 h-3.5" /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email-link"
                  href={`mailto:${EMAIL}`}
                  className="inline-flex items-center gap-2 text-white/80 hover:text-white break-all"
                >
                  <Mail className="w-3.5 h-3.5" /> {EMAIL}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp-link"
                  href={buildWhatsAppUrl(t.whatsapp_pretext)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[hsl(var(--red-2))] hover:opacity-80"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">Language</div>
            <div className="flex gap-2">
              {["en", "es", "nl"].map((l) => (
                <button
                  key={l}
                  data-testid={`footer-lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-2 border text-xs uppercase tracking-[0.22em] transition-colors ${
                    lang === l
                      ? "border-[hsl(var(--red-2))] text-[hsl(var(--red-2))]"
                      : "border-white/15 text-white/65 hover:border-white/40"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">Follow</div>
              <a
                href="https://instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"
              >
                <Instagram className="w-4 h-4" /> @6inqtattooz
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-[11px] uppercase tracking-[0.24em] text-white/40">
          <span>© {new Date().getFullYear()} 6inq Tattooz. {t.footer.rights}</span>
          <span>Joweney Balentien · Curaçao × Rotterdam</span>
        </div>
      </div>
    </footer>
  );
}
